/**
 * LibraryManagerView — Task 9: gestão da biblioteca de Respostas Prontas.
 *
 * Rota `/admin/ia/quick-replies` quando `canned-response-manage` está
 * presente (gate-and-split em `QuickRepliesPage`, decisão do controller —
 * inércia de produção: sem a cap, o catálogo LEGADO por-playbook continua
 * intacto). Tabela: título · categoria · atalho · resumo dos items · playbooks
 * vinculados · ações (editar/arquivar). Busca client-side por título/categoria/
 * atalho. v1 funcional — o polimento visual é rito Claude Design na S2.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Archive, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  useArchiveCannedResponse,
  useCannedResponses,
  useMediaAssets,
} from '../../hooks/use-canned-responses';
import { ApiError, type CannedResponseWire } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';
import { usePlaybookList } from '../playbooks/use-playbooks';

import { CannedResponseEditModal } from './CannedResponseEditModal';
import { summarizeCannedResponseItems } from './items-summary';

import type { JSX } from 'react';

type FormState = { mode: 'create' } | { mode: 'edit'; response: CannedResponseWire } | null;

function matchesQuery(response: CannedResponseWire, query: string): boolean {
  if (query.trim() === '') return true;
  const q = query.trim().toLowerCase();
  return (
    response.title.toLowerCase().includes(q) ||
    (response.category ?? '').toLowerCase().includes(q) ||
    (response.shortcut ?? '').toLowerCase().includes(q)
  );
}

export function LibraryManagerView(): JSX.Element {
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const qc = useQueryClient();
  const listQ = useCannedResponses(true);
  const mediaQ = useMediaAssets(true);
  const playbooks = usePlaybookList();
  const archiveMut = useArchiveCannedResponse();

  const mediaById = useMemo(
    () => new Map((mediaQ.data?.data ?? []).map((m) => [m.id, m])),
    [mediaQ.data],
  );
  const playbookNameById = useMemo(
    () => new Map(playbooks.data.map((p) => [p.id, p.name])),
    [playbooks.data],
  );

  if (listQ.isLoading) {
    return (
      <div className="px-6 py-5">
        <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
          Carregando respostas prontas…
        </p>
      </div>
    );
  }

  if (listQ.isError) {
    return (
      <div className="px-6 py-5">
        <p className="mb-3 text-sm" role="alert" style={{ color: 'var(--danger-fg)' }}>
          Falha ao carregar a biblioteca de respostas prontas.
        </p>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => void listQ.refetch()}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const all = listQ.data?.data ?? [];
  const filtered = all.filter((r) => matchesQuery(r, query));

  const handleArchive = (response: CannedResponseWire): void => {
    archiveMut.mutate(
      { id: response.id, version: response.version },
      {
        onSuccess: () => {
          setConfirmingId(null);
          useToastStore
            .getState()
            .push({ variant: 'success', title: `"${response.title}" arquivada` });
        },
        onError: (err) => {
          setConfirmingId(null);
          if (err instanceof ApiError && err.status === 409) {
            // M2 — a copy do toast promete "recarregamos as respostas"; sem
            // invalidar a query isso era mentira (a lista ficava stale até
            // o próximo fetch orgânico). Invalidar torna a copy verdadeira.
            void qc.invalidateQueries({ queryKey: ['canned-responses'] });
            useToastStore.getState().push({
              variant: 'info',
              title: 'A lista foi atualizada por outra alteração',
              description: 'Recarregamos as respostas. Tente novamente.',
            });
            return;
          }
          useToastStore
            .getState()
            .push({ variant: 'error', title: 'Não foi possível arquivar a resposta.' });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold" style={{ color: 'var(--fg-1)' }}>
            Respostas prontas
          </h1>
          <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
            {all.length === 0
              ? 'Nenhuma resposta cadastrada ainda.'
              : `${all.length.toString()} ${all.length === 1 ? 'resposta' : 'respostas'} na biblioteca`}
          </p>
        </div>
        <input
          type="search"
          className="ml-auto rounded border px-2 py-1 text-sm"
          style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
          placeholder="Buscar título, categoria ou atalho…"
          aria-label="Buscar respostas prontas"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setForm({ mode: 'create' })}
        >
          <Plus size={14} aria-hidden /> Nova resposta
        </button>
      </div>

      {all.length === 0 ? (
        <div
          className="rounded-md px-4 py-8 text-center text-sm"
          style={{ border: '1px dashed var(--border-default)', color: 'var(--fg-3)' }}
        >
          Nenhuma resposta na biblioteca — crie a primeira.
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
          Nenhuma resposta corresponde à busca “{query}”.
        </p>
      ) : (
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
              <th className="px-2 py-2 text-left">Título</th>
              <th className="px-2 py-2 text-left">Categoria</th>
              <th className="px-2 py-2 text-left">Atalho</th>
              <th className="px-2 py-2 text-left">Itens</th>
              <th className="px-2 py-2 text-left">Playbooks</th>
              <th className="px-2 py-2 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((response) => (
              <tr key={response.id} style={{ borderBottom: '1px solid var(--border-default)' }}>
                <td className="px-2 py-2 font-medium" style={{ color: 'var(--fg-1)' }}>
                  {response.title}
                </td>
                <td className="px-2 py-2" style={{ color: 'var(--fg-2)' }}>
                  {response.category ?? '—'}
                </td>
                <td className="px-2 py-2">
                  {response.shortcut !== null && response.shortcut !== '' ? (
                    <kbd>/{response.shortcut}</kbd>
                  ) : (
                    <span style={{ color: 'var(--fg-3)' }}>—</span>
                  )}
                </td>
                <td className="px-2 py-2" style={{ color: 'var(--fg-2)' }}>
                  {summarizeCannedResponseItems(response.items, mediaById)}
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    {response.playbookIds.length === 0 ? (
                      <span style={{ color: 'var(--fg-3)' }}>—</span>
                    ) : (
                      response.playbookIds.map((pid) => (
                        <span
                          key={pid}
                          className="rounded-full px-2 py-0.5 text-xs"
                          style={{
                            background: 'var(--bg-surface-2)',
                            border: '1px solid var(--border-default)',
                          }}
                        >
                          {playbookNameById.get(pid) ?? pid}
                        </span>
                      ))
                    )}
                  </div>
                </td>
                <td className="px-2 py-2">
                  {confirmingId === response.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                        Arquivar?
                      </span>
                      <button
                        type="button"
                        className="btn btn--destructive btn--sm"
                        disabled={archiveMut.isPending}
                        onClick={() => handleArchive(response)}
                      >
                        Confirmar
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={archiveMut.isPending}
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        aria-label={`Editar ${response.title}`}
                        onClick={() => setForm({ mode: 'edit', response })}
                      >
                        <Pencil size={13} aria-hidden /> Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        aria-label={`Arquivar ${response.title}`}
                        onClick={() => setConfirmingId(response.id)}
                      >
                        <Archive size={13} aria-hidden /> Arquivar
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form !== null && (
        <CannedResponseEditModal
          mode={form.mode}
          {...(form.mode === 'edit' && { response: form.response })}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}
